<template>
  <div class="chemtracker-module" data-test="outside-vif">
    <div v-if="chemicalContainer">
      <ChemicalContainerDetails :chemical-container="chemicalContainer" />
      <SdsSearchResultsListing
        @reset-sds-selection="resetSdsSelection"
        :sds-search-results="sdsSearchResults"
        ref="sdsSearchResults"
        :is-active="resultsListingActive"
      />

      <div class="action-items">
        <p>
          Relevant SDS not listed above? Choose from options below to select a
          new SDS for the chemical.
        </p>

        <div v-if="hasErrors()" class="messages error messages--error">
          <b>Please correct the following error(s):</b>
          <ul>
            <li v-for="error in getErrors()">
              {{ error }}
            </li>
          </ul>
        </div>

        <b-form
          ref="manualSdsAttachment"
          id="chemtracker-sds-attachments-ui-manual"
        >
          <b-radio-group @change="clearSelectedSdsDocumentId()">
            <b-form-radio
              v-model="selectedSdsOption"
              value="sds_file"
              class="form-group"
            >
              Upload SDS
            </b-form-radio>
            <div
              v-if="selectedSdsOption === 'sds_file'"
              class="sds-manual-entry-wrapper"
            >
              <p>
                SDS uploaded here remains exclusively on your organization's
                instance of ChemTracker, and are not automatically shared with a
                centralized database. This SDS will be available to anyone who
                may access containers of these chemicals or products
              </p>
              <b-form-file
                v-model="file1"
                ref="file1"
                :state="Boolean(file1)"
                accept=".pdf"
                size="sm"
                name="sds_file"
                required
                placeholder="Allowed file extensions: pdf. 20mb file size limit."
              />

              <div class="mt-3">
                Selected file: {{ file1 ? file1.name : "" }}
              </div>
              <b-row>
                <b-col>
                  <div class="form-group required">
                    <label
                      for="sds-file-source"
                      class="raftjs-entity-view-label"
                      >Source: <sup>*</sup
                      ><b-img
                        id="sds-file-source-help"
                        class="help-icon"
                        :src="getHelpIconPath()"
                        alt="help"
                    /></label>
                  </div>
                  <b-popover
                    target="sds-file-source-help"
                    triggers="hover focus"
                  >
                    <template slot="title"> SDS Source </template>
                    Please enter the manufacturer name or other name or title of
                    where this URL came from, up to 255 characters (including
                    spaces).
                  </b-popover>
                  <b-input
                    v-model="submittedSdsSource"
                    type="text"
                    name="sds-file-source"
                    id="sds-file-source"
                    aria-label="Enter SDS source"
                    size="sm"
                    maxlength="255"
                    placeholder="Enter the source for the SDS file"
                    :class="{
                      'is-invalid error':
                        sdsEntityErrors.hasOwnProperty('sds-file-source'),
                    }"
                  />
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <div class="form-group required">
                    <label
                      for="file-sds-document-date"
                      class="raftjs-entity-view-label"
                      >SDS Document Date: <sup>*</sup></label
                    >
                  </div>
                  <SciSureDatePicker
                    :record-date="submittedSdsDocumentDate"
                    :is-required="true"
                    :is-max-date="true"
                    name="file-sds-document-date"
                    id="file-sds-document-date"
                    @update-record-date="updateDocumentDate"
                  />
                </b-col>
              </b-row>
            </div>

            <b-form-radio
              v-model="selectedSdsOption"
              value="sds_url"
              class="form-group"
            >
              Assign SDS as URL/link
            </b-form-radio>
            <div
              v-if="selectedSdsOption === 'sds_url'"
              class="sds-manual-entry-wrapper"
            >
              <b-row
                :class="{
                  'is-invalid error':
                    sdsEntityErrors.hasOwnProperty('sds-url-url'),
                }"
              >
                <b-col>
                  <div class="form-group required">
                    <label for="sds-url-url" class="raftjs-entity-view-label"
                      >SDS URL: <sup>*</sup></label
                    >
                  </div>
                  <b-input
                    v-model="submittedSdsUrl"
                    type="url"
                    name="sds-url-url"
                    id="sds-url-url"
                    aria-label="Enter SDS URL"
                    size="sm"
                    placeholder="Enter the URL for the SDS file"
                    :class="{
                      'is-invalid error':
                        sdsEntityErrors.hasOwnProperty('sds-url-url'),
                    }"
                  />
                </b-col>
              </b-row>
              <b-row
                :class="{
                  'is-invalid error':
                    sdsEntityErrors.hasOwnProperty('sds-url-title'),
                }"
              >
                <b-col>
                  <div class="form-group required">
                    <label for="sds-url-title" class="raftjs-entity-view-label"
                      >Title: <sup>*</sup
                      ><b-img
                        id="sds-url-title-help"
                        class="help-icon"
                        :src="getHelpIconPath()"
                        alt="help"
                    /></label>
                  </div>
                  <b-popover target="sds-url-title-help" triggers="hover focus">
                    <template slot="title"> SDS Title </template>
                    Please enter a title or short description, up to 255
                    characters (including spaces).
                  </b-popover>
                  <b-input
                    v-model="submittedSdsTitle"
                    type="text"
                    required
                    aria-required="true"
                    id="sds-url-title"
                    name="sds-url-title"
                    aria-label="Enter SDS title"
                    size="sm"
                    maxlength="255"
                    placeholder="Enter the title for the SDS URL"
                    :class="{
                      'is-invalid error':
                        sdsEntityErrors.hasOwnProperty('sds-url-title'),
                    }"
                  />
                </b-col>
              </b-row>
              <b-row
                :class="{
                  'is-invalid error':
                    sdsEntityErrors.hasOwnProperty('sds-url-source'),
                }"
              >
                <b-col>
                  <div class="form-group required">
                    <label for="sds-url-source" class="raftjs-entity-view-label"
                      >Source: <sup>*</sup
                      ><b-img
                        id="sds-url-source-help"
                        class="help-icon"
                        :src="getHelpIconPath()"
                        alt="help"
                    /></label>
                  </div>
                  <b-popover
                    target="sds-url-source-help"
                    triggers="hover focus"
                  >
                    <template slot="title"> SDS Source </template>
                    Please enter the manufacturer name or other name or title of
                    where this URL came from, up to 255 characters (including
                    spaces).
                  </b-popover>
                  <b-input
                    v-model="submittedSdsSource"
                    type="text"
                    name="sds-url-source"
                    id="sds-url-source"
                    aria-label="Enter SDS source"
                    size="sm"
                    maxlength="255"
                    placeholder="Enter the source for the SDS URL"
                    :class="{
                      'is-invalid error':
                        sdsEntityErrors.hasOwnProperty('sds-url-source'),
                    }"
                  />
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <div class="form-group required">
                    <label
                      for="url-sds-document-date"
                      class="raftjs-entity-view-label"
                      >SDS Document Date: <sup>*</sup></label
                    >
                  </div>
                  <SciSureDatePicker
                    :record-date="submittedSdsDocumentDate"
                    :is-required="true"
                    :is-max-date="true"
                    name="url-sds-document-date"
                    id="url-sds-document-date"
                    @update-record-date="updateDocumentDate"
                  />
                </b-col>
              </b-row>
            </div>
          </b-radio-group>
        </b-form>
      </div>

      <div class="sds-attachments-dialog-controls" v-if="!hideControls">
        <button v-if="allowAttach" @click="nextStep">Attach SDS</button>
        <button @click="closeDialog">Cancel</button>
      </div>
      <div v-if="hideControls" class="text-center text-danger my-2">
        <div v-if="$isB4" class="loading-throbber">
          <b-spinner class="align-middle" />
        </div>
        <div v-else>
          <img
            src="/sites/all/modules/hide_submit/images/squares.gif"
            alt="loading"
          />
        </div>
      </div>
    </div>
    <div v-else>
      <RaftBusy busy-text="Loading..." />
    </div>
  </div>
</template>

<script>
import Jsona from "jsona";
import { jsonClient, restClient } from "@/lib/raftApi";
import { mapGetters, mapActions } from "vuex";
import SciSureDatePicker from "@/components/raft/SciSureDatePicker.vue";
import ChemicalContainerDetails from "../ChemicalContainerDetails.vue";
import SdsSearchResultsListing from "./SdsSearchResultsListing.vue";
import PathMapper from "../../../lib/PathMapper";
import raft_generic_helper from "../../../lib/raft_generic_helper";
import RaftBusy from "../../RaftBusy.vue";

const dataFormatter = new Jsona();

export default {
  name: "SdsAttachmentsFirstStep",
  inject: ["selectedContainerUuid", "contextNid"],
  data() {
    return {
      raft_generic_helper: raft_generic_helper,
      sdsEntityErrors: {},
      hideControls: false,
      showSelections: true,
      chemicalContainer: null,
      sdsSearchResults: null,
      selectedSdsOption: "sds_external",
      submittedSdsUrl: "",
      submittedSdsTitle: "",
      submittedSdsSource: "",
      submittedSdsDocumentDate: null,
      selectedSearchResult: null,
      file1: null,
      errors: [],
      disabledDates: {
        from: new Date(),
      },
      resultsListingActive: true,
    };
  },
  components: {
    ChemicalContainerDetails,
    SdsSearchResultsListing,
    RaftBusy,
    SciSureDatePicker,
  },
  computed: {
    ...mapGetters(["getSelectedSdsEntityUuid", "getSelectedSdsDocumentId"]),
    allowAttach: function () {
      switch (this.selectedSdsOption) {
        case "sds_url":
          return (
            this.submittedSdsUrl &&
            this.submittedSdsTitle &&
            this.submittedSdsSource &&
            this.submittedSdsDocumentDate
          );

        case "sds_file":
          return (
            this.submittedSdsSource &&
            this.file1 &&
            this.submittedSdsDocumentDate
          );

        case "sds_external":
          return this.getSelectedSdsEntityUuid || this.getSelectedSdsDocumentId;

        default:
          return false;
      }
    },
  },
  methods: {
    ...mapActions([
      "updateShowSdsModal",
      "updateSelectedSdsDocumentId",
      "updateSelectedSdsEntityUuid",
      "updateSelectedSdsEntityType",
      "updateSelectedContainer",
      "updateSdsModalTitle",
    ]),
    hasErrors: function () {
      return Object.keys(this.sdsEntityErrors).length !== 0;
    },
    resetSdsSelection: function (newSelection) {
      this.selectedSdsOption = "sds_external";
      this.selectedSearchResult = newSelection;
      this.sdsEntityErrors = {};
      this.submittedSdsUrl = "";
      this.submittedSdsTitle = "";
      this.submittedSdsSource = "";
      this.submittedSdsDocumentDate = new Date();
      this.updateSelectedSdsEntityUuid("");
      this.file1 = null;
    },
    getHelpIconPath: function () {
      return PathMapper.getIconAssetPath("help.png", this.$isB4);
    },
    getCsvIconPath: function () {
      return PathMapper.getIconAssetPath("CSV.svg", this.$isB4);
    },
    closeDialog: function () {
      this.updateShowSdsModal(false);
    },
    previousStep: function () {
      this.$emit("stepper-change", 1);
    },
    nextStep: function () {
      this.hideControls = true;
      if (this.checkRequiredFields()) {
        const sds_entity_uuid = this.getSelectedSdsEntityUuid;
        if (sds_entity_uuid) {
          this.updateChemicalContainer();
        } else {
          // Create the SDS entity, and also update the chemical container.
          this.createSdsEntity();
        }
      } else {
        this.$showNotification("error", "Form validation error");
        this.hideControls = false;
      }
      this.resultsListingActive = false;
    },
    checkRequiredFields() {
      this.sdsEntityErrors = {};
      if (this.selectedSdsOption === "sds_url") {
        if (!this.submittedSdsUrl) {
          this.sdsEntityErrors["sds-url-url"] = ["You must enter a valid url."];
        } else if (!raft_generic_helper.validateUrl(this.submittedSdsUrl)) {
          this.sdsEntityErrors["sds-url-url"] = ["You must enter a valid url."];
        }

        if (!this.submittedSdsSource) {
          this.sdsEntityErrors["sds-url-source"] = [
            "You must enter a source for this url.",
          ];
        }

        if (!this.submittedSdsTitle) {
          this.sdsEntityErrors["sds-url-title"] = [
            "You must enter a title for this url.",
          ];
        }

        if (!this.submittedSdsDocumentDate) {
          this.sdsEntityErrors["url-sds-document-date"] = [
            "You must enter the date of the SDS document",
          ];
        }
      }
      if (this.selectedSdsOption === "sds_file") {
        const errors = [];
        let hasErrors = false;
        if (!this.submittedSdsSource) {
          hasErrors = true;
          errors.push("You must enter a source for this file.");
        }
        if (!this.file1) {
          hasErrors = true;
          errors.push("You must upload a file.");
        }

        if (this.file1.size > 20971520) {
          hasErrors = true;
          errors.push(
            "The file you selected is larger than the allowed file size of 20MB.",
          );
        }

        if (hasErrors) {
          this.sdsEntityErrors.sds_file = errors;
        }

        if (!this.submittedSdsDocumentDate) {
          this.sdsEntityErrors["file-sds-document-date"] = [
            "You must enter the date of the SDS document",
          ];
        }
      }
      if (this.selectedSdsOption === "sds_external") {
        if (this.getSelectedSdsDocumentId === "") {
          this.sdsEntityErrors.sds_external = [
            "An error occurred with the selected SDS search result.",
          ];
        }
      }
      return Object.keys(this.sdsEntityErrors).length === 0;
    },
    validateFileSize: function () {
      if (this.file1.size > 20971520) {
        this.sdsEntityErrors.file1 = [
          "The file you selected is larger than the allowed file size of 20MB.",
        ];
        this.file1 = null;
        this.$refs.file1.reset();
      } else if (this.sdsEntityErrors.hasOwnProperty("file1")) {
        delete this.sdsEntityErrors.file1;
      }
    },
    createSdsEntity: function () {
      // eslint-disable-next-line default-case
      switch (this.selectedSdsOption) {
        case "sds_url":
          this.buildSdsUrlData();
          break;

        case "sds_file":
          this.buildSdsFileData();
          break;

        case "sds_external":
          this.buildSdsExternalData();
          break;
      }
    },
    async postSdsMetaData(data) {
      this.isBusy = true;
      this.hideControls = true;
      const path = "raft_metadata/" + this.selectedSdsOption;
      const queries = {};
      const jsonResponseData = await jsonClient
        .post({ path, queries, data })
        .catch((e) => this.handleSdsPostError(e));
      if (jsonResponseData) {
        this.updateSelectedSdsEntityUuid(jsonResponseData.data?.id);
        this.updateSelectedSdsEntityType(
          jsonResponseData.data?.type?.replace(/raft_metadata--/, ""),
        );
        this.updateChemicalContainer();
        this.hideControls = false;
      }
    },
    buildSdsUrlData: function () {
      this.postSdsMetaData({
        type: "raft_metadata--sds_url",
        attributes: {
          field_chemdb_type_related_url: {
            uri: this.submittedSdsUrl,
          },
          field_current: true,
          field_description: this.submittedSdsTitle,
          field_source: this.submittedSdsSource,
          field_sds_document_date: raft_generic_helper.datepickerValueToIso(
            this.submittedSdsDocumentDate,
          ),
        },
        relationships: {
          association: {
            data: {
              type: "node--chemdb_type",
              id: this.chemicalContainer?.field_chemical_type?.id,
            },
          },
        },
      });
    },
    buildSdsExternalData: function () {
      const selectedSearchResult = this.selectedSearchResult;
      let document_date = null;
      if (
        selectedSearchResult.hasOwnProperty("changed") &&
        selectedSearchResult.changed
      ) {
        const changed = selectedSearchResult.changed.split(" ").join("T");
        document_date = raft_generic_helper.datepickerValueToIso(changed);
      }

      this.postSdsMetaData({
        type: "raft_metadata--sds_external",
        attributes: {
          field_sds_document_id: selectedSearchResult.id,
          field_current: true,
          field_sds_provider: "",
          field_description: selectedSearchResult.chemical_name,
          field_source: selectedSearchResult.manufacturer_name,
          field_sds_document_date: document_date,
        },
        relationships: {
          association: {
            data: {
              type: "node--chemdb_type",
              id: this.chemicalContainer?.field_chemical_type?.id,
            },
          },
        },
      });
    },
    async buildSdsFileData() {
      const sdsTitle =
        "SDS For " + this.chemicalContainer.field_chemical_name_preferred;
      const submittedSdsSource = this.submittedSdsSource;
      const submittedSdsDocumentDate = raft_generic_helper.datepickerValueToIso(
        this.submittedSdsDocumentDate,
      );
      const conId = this.chemicalContainer?.field_chemical_type?.id;
      const path = "raft_metadata/sds_file/field_chemdb_type_related_file";
      const queries = {};
      const file = this.file1;
      const jsonResponseData = await jsonClient
        .postFile({ path, queries, file })
        .catch((e) => {
          this.handleSdsPostError(e);
          this.$showNotification("error", e.message);
        });
      if (jsonResponseData?.data.hasOwnProperty("id")) {
        const newData = {
          type: "raft_metadata--sds_file",
          attributes: {
            field_current: true,
            field_description: sdsTitle,
            field_source: submittedSdsSource,
            field_sds_document_date: submittedSdsDocumentDate,
          },
          relationships: {
            association: {
              data: {
                type: "node--chemdb_type",
                id: conId,
              },
            },
            field_chemdb_type_related_file: {
              data: {
                type: "file--file",
                id: jsonResponseData.data.id,
              },
            },
          },
        };
        this.postSdsMetaData(newData);
      }
    },
    handleSdsPostError: function ({ message, data }) {
      this.sdsEntityErrors.sds_creation = [
        "An error occurred while attaching the selected SDS.",
        message,
      ];
      if (data && data.errors) {
        for (let i = 0; i < data.errors.length; i++) {
          this.sdsEntityErrors.sds_creation.push(data.errors[i].detail);
        }
      }
      this.isBusy = false;
      this.hideControls = false;
    },
    async updateChemicalContainer() {
      const data = {
        id: this.selectedContainerUuid,
        type: "node--chemical_container",
        relationships: {
          field_chemical_sds: {
            data: {
              type: "raft_metadata--" + this.selectedSdsOption,
              id: this.getSelectedSdsEntityUuid,
            },
          },
        },
      };
      const path = "node/chemical_container/" + this.selectedContainerUuid;
      const queries = {};
      const jsonResponseData = await jsonClient
        .patch({ path, queries, data })
        .catch((e) => {
          this.errors.sds_creation = [
            "An error occurred while attaching the selected SDS.",
            e.message,
          ];
          this.$showNotification("error", e.message);
        });
      if (jsonResponseData) {
        this.updateSelectedContainer(
          dataFormatter.deserialize(jsonResponseData),
        );
        this.$emit("stepper-change", 2);
      }
    },
    async fetchChemicalContainer() {
      const filters = {
        include: "field_chemical_type, field_chemical_manufacturer",
        "fields[chemdb_manufacturer--chemdb_manufacturer]": "name",
        "fields[node--chemical_container]":
          "field_chemical_type,field_chemical_manufacturer,field_chemical_name_preferred,field_chemical_product_name,field_chemical_container_id,field_chemical_product_number",
      };
      const path = "node/chemical_container/" + this.selectedContainerUuid;
      const queries = filters;
      const jsonResponseData = await jsonClient
        .get({ path, queries })
        .catch((e) => {
          this.errors.fetch_chemical_container = [
            "An error occurred while fetching the selected chemical container.",
            e.message,
          ];
          this.$showNotification("error", e.message);
        });
      if (jsonResponseData) {
        this.chemicalContainer = dataFormatter.deserialize(jsonResponseData);
        this.updateSelectedContainer(this.chemicalContainer);
        this.fetchSdsResults();
      }
    },
    async fetchSdsResults() {
      this.sdsSearchResults = null;
      const path = "/chemtracker-sds-search";
      const queries = {
        chemdb_type_uuid: this.chemicalContainer?.field_chemical_type?.id,
      };
      const restResponseData = await restClient
        .get({ path, queries })
        .catch((e) => {
          this.sdsSearchResults = [];
          // eslint-disable-next-line no-console
          console.error(e);
          this.$showNotification("error", e.message);
        });
      if (restResponseData) {
        if (restResponseData.hasOwnProperty("sds_results")) {
          this.sdsSearchResults = restResponseData.sds_results;
        } else {
          this.sdsSearchResults = [];
        }
      }
    },
    clearSelectedSdsDocumentId: function () {
      this.$refs.sdsSearchResults.selectedSdsResult = null;
    },
    getErrors: function () {
      const allErrors = [];
      const fields = Object.keys(this.sdsEntityErrors);
      for (let i = 0; i <= fields.length; i++) {
        if (typeof this.sdsEntityErrors[fields[i]] !== "undefined") {
          for (let j = 0; j < this.sdsEntityErrors[fields[i]].length; j++) {
            allErrors.push(this.sdsEntityErrors[fields[i]][j]);
          }
        }
      }
      return allErrors;
    },
    hasError: function (field) {
      return this.sdsEntityErrors[field].length > 0;
    },
    updateDocumentDate(date) {
      this.submittedSdsDocumentDate =
        raft_generic_helper.datepickerValueToIso(date);
    },
  },
  watch: {
    file1(newFile) {
      if (newFile) {
        this.validateFileSize();
      }
    },
  },
  created() {
    this.fetchChemicalContainer();
  },
  mounted() {
    this.updateSdsModalTitle("Assign SDS to Container");
    this.resetSdsSelection(null);
  },
};
</script>

<style scoped></style>
